import type { CSSProperties } from 'react';

type FlowablePageBreakGuideStyle = CSSProperties & {
    '--rb-flowable-page-height': string;
    '--rb-flowable-page-margin': string;
};

interface FlowablePageBreakGuidesProps {
    pageHeight: string;
    pageMargin: string;
}

export function createFlowablePageBreakGuideStyle(
    pageHeight: string,
    pageMargin: string
): FlowablePageBreakGuideStyle {
    return {
        '--rb-flowable-page-height': pageHeight,
        '--rb-flowable-page-margin': pageMargin,
        // The editor represents one continuous logical page. Start at the end of the
        // first printable content box, then repeat by the printable content height.
        // Chrome may move a real break earlier to keep an element or table row together.
        top: 'calc(var(--rb-flowable-page-height) - var(--rb-flowable-page-margin))',
        backgroundImage:
            'radial-gradient(circle at 1px 1px, rgb(100 116 139 / 0.65) 1px, transparent 1.5px)',
        backgroundPosition: 'left top',
        backgroundSize:
            '8px calc(var(--rb-flowable-page-height) - var(--rb-flowable-page-margin) - var(--rb-flowable-page-margin))',
    };
}

export function FlowablePageEditorNotice(): JSX.Element {
    return (
        <div
            role="note"
            data-rb-flowable-page-notice="true"
            className="mx-auto mb-2 flex w-fit max-w-full flex-col gap-0.5 rounded border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900 shadow-sm"
        >
            <span>{gettext('FLOWABLE_PAGE_EDITOR_NOTICE')}</span>
            <span className="text-[10px] text-sky-700">
                {gettext('FLOWABLE_PAGE_BREAK_GUIDE_NOTICE')}
            </span>
        </div>
    );
}

export function FlowablePageBreakGuides({
    pageHeight,
    pageMargin,
}: FlowablePageBreakGuidesProps): JSX.Element {
    const guideStyle = createFlowablePageBreakGuideStyle(pageHeight, pageMargin);

    return (
        <div
            aria-hidden={true}
            data-rb-flowable-page-break-guides="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 z-[1]"
            style={guideStyle}
        />
    );
}
